/* --------------------------------------------------------------------------
 * Footer.jsx  – full, drop‑in replacement
 * --------------------------------------------------------------------------
 * • Keeps original logo / links / socials.
 * • Injects a working newsletter form that stores subscribers in DB
 *   (see useNewsletter hook).
 * • Uses i18n keys identical to the original snippet.
 * ------------------------------------------------------------------------ */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import footerLogo from "../assets/images/logo/logo-footer.webp";
import useNewsletter from "../hooks/useNewsletter";

const menuRoutes = {
  home: "/",
  services: "/service",
  about: "/about",
  special_needs: "/special-needs",
  blog: "/blogs",
};

// ---------------------------------------------------------------------------
// Newsletter sub‑component (inline for simplicity)
// ---------------------------------------------------------------------------
function NewsletterForm() {
  const { t } = useTranslation();
  const { status, subscribe } = useNewsletter();
  const [email, setEmail] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    subscribe({ email });
  };

  return (
    <div className="single-footer">
      <h4 className="mb-8 text-2xl font-bold text-white">
        {t("footer.newsletter.title")}
      </h4>

      <div className="mb-8 text-white">
        {t("footer.newsletter.description", { count: 68000 })}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4 flex items-center rounded-md bg-white py-[10px] pr-[10px] pl-6 shadow-e1">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("footer.newsletter.placeholder")}
            className="w-full border-none focus:ring-0"
          />
        </div>
        <button className="btn btn-primary block w-full text-center">
          {t("footer.newsletter.button")}
        </button>
      </form>

      {status === "success" && (
        <p className="mt-4 text-green-400">{t("footer.newsletter.success")}</p>
      )}
      {status === "error" && (
        <p className="mt-4 text-red-400">{t("footer.newsletter.error")}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Footer
// ---------------------------------------------------------------------------
export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-black bg-[url('../images/all-img/footer-bg-1.png')] bg-cover bg-center bg-no-repeat">
      <div className="section-padding container">
        <div className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-4">
          {/* 1) Brand & socials */}
          <div className="single-footer">
            <div className="lg:max-w-[270px]">
              <a href="/" className="mb-10 block">
                <img src={footerLogo} alt="Footer Logo" />
              </a>
              <p className="text-white">{t("footer.description")}</p>

              <ul className="flex space-x-4 pt-8">
                {[
                  {
                    href: "https://www.facebook.com/online.institute11",
                    icon: "bxl:facebook",
                  },
                  {
                    href: "https://www.tiktok.com/@online_institutekw?_t=8jShYhf9fV4&_r=1",
                    icon: "ic:baseline-tiktok",
                  },
                  {
                    href: "https://www.instagram.com/online_institute/",
                    icon: "bxl:instagram",
                  },
                  {
                    href: "https://t.snapchat.com/jHxZSl9c",
                    icon: "bxl:snapchat",
                  },
                ].map(({ href, icon }) => (
                  <li key={icon}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mr-2 flex h-12 w-12 items-center justify-center rounded bg-white bg-opacity-[0.08] text-2xl text-white transition hover:bg-primary"
                    >
                      <iconify-icon icon={icon}></iconify-icon>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 2) Quick links */}
          <div className="single-footer">
            <div className="flex space-x-[50px]">
              <div className="flex-1 lg:flex-none">
                <h4 className="mb-8 text-2xl font-bold text-white">
                  {t("footer.links")}
                </h4>
                <ul className="list-item space-y-5">
                  {Object.keys(t("footer.menu", { returnObjects: true })).map(
                    (key) => (
                      <li key={key}>
                        <a
                          href={menuRoutes[key] || "#"}
                          className="text-white transition hover:text-primary"
                        >
                          {t(`footer.menu.${key}`)}
                        </a>
                      </li>
                    )
                  )}
                </ul>
              </div>
            </div>
          </div>

          {/* 3) Legal / contact */}
          <div className="single-footer">
            <h4 className="mb-8 text-2xl font-bold text-white">
              {t("footer.legal")}
            </h4>
            <ul className="list-item space-y-5">
              <li className="text-white">
                {t("footer.contact.contact")}
                <br />
                <a
                  href="tel:+96550761835"
                  className="ml-1 mt-2 underline transition hover:text-primary"
                  dir="ltr"
                >
                  +965&nbsp;507&nbsp;61835
                </a>
              </li>
              <li className="text-white">
                {t("footer.contact.email")}
                <br />
                <a
                  href="mailto:info@onlineinstitute.net"
                  className="ml-1 mt-2 underline transition hover:text-primary"
                >
                  info@onlineinstitute.net
                </a>
              </li>
            </ul>
          </div>

          {/* 4) Newsletter */}
          <NewsletterForm />
        </div>
      </div>

      {/* bottom strip */}
      <div className="container border-t border-white border-opacity-[0.1] py-8 text-center text-base text-white">
        &copy; {t("footer.copyright", { year })}
      </div>
    </footer>
  );
}
